/**
 * features/letters/lettersWrite.ts
 * Archive company letters to the companyDocuments collection with a
 * sequential reference (ACE-LTR-YYYY-####). The reference counter is
 * an atomic per-year transaction on the counters collection.
 */
import { collection, deleteDoc, doc, runTransaction } from 'firebase/firestore';
import { db, addDoc } from '@/lib/firebase';
import { COLLECTIONS, type CompanyDocument, type LetterKind } from '@/types';

export interface LetterPayload {
  kind: LetterKind;
  subject: string;
  recipient: string;
  body: string;
  date: string;
  signed: boolean;
  stamped: boolean;
  signatoryName?: string;
  signatoryTitle?: string;
}

async function nextLetterReference(when: Date): Promise<string> {
  const year = when.getFullYear();
  const ref = doc(db, COLLECTIONS.counters, 'letter');
  const seq = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const data = snap.exists() ? (snap.data() as { year?: number; seq?: number }) : {};
    const nextSeq = data.year === year ? (data.seq ?? 0) + 1 : 1;
    tx.set(ref, { year, seq: nextSeq });
    return nextSeq;
  });
  return `ACE-LTR-${year}-${String(seq).padStart(4, '0')}`;
}

export async function saveLetter(p: LetterPayload, actor: string): Promise<{ id: string; reference: string }> {
  const reference = await nextLetterReference(new Date(p.date));
  const now = new Date().toISOString();
  const data: Omit<CompanyDocument, 'id'> = {
    kind: p.kind,
    reference,
    subject: p.subject,
    recipient: p.recipient,
    body: p.body,
    date: p.date,
    signed: p.signed,
    stamped: p.stamped,
    ...(p.signatoryName ? { signatoryName: p.signatoryName } : {}),
    ...(p.signatoryTitle ? { signatoryTitle: p.signatoryTitle } : {}),
    createdAt: now,
    createdBy: actor,
  };
  const refDoc = await addDoc(collection(db, COLLECTIONS.companyDocuments), data);
  return { id: refDoc.id, reference };
}

export async function deleteLetter(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.companyDocuments, id));
}
